import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import { DALNotFoundError } from '@classpass/dal';
import { replace as replacePath } from 'react-router-redux';
import { all, call, put, takeEvery, select } from 'redux-saga/effects';
import * as logger from 'universal-logger';
import { glsLocation, msas, cms } from 'redux/saga/entities/bound';
import { getUserLocation } from 'redux/saga/helpers';
import { persistUserLocation } from 'redux/saga/helpers/location';
import { getMsaFromPath } from 'redux/saga/helpers/msas';
import { pathSelector } from 'redux/saga/selectors/request';
import { msaById } from 'redux/saga/selectors/msas';
import { addGenericNotification, addFailureNotification } from 'features/Banners/Banners.reducer';
import { GET_CLASSPASS_DEFAULT_PROMOTION_ALIAS } from 'constants/promotions';
import { openLocationModal } from 'containers/LocationModal/location.saga';
import { setMsaId } from 'redux/modules/features/homepage';
import { userErrorMessages as messages } from 'components/Errors/Error.messages';

export const changeLocation = (lat, lon) => ({
  type: 'CHANGE_LOCATION',
  lat,
  lon,
});

export function* doBuildNewPath(client, { path, newMsaId }) {
  const [msaList, oldMsa] = yield all([call(fetchEntityIfNeeded, msas, [client]), call(getMsaFromPath, client, path)]);
  const newMsa = msaById(msaList, newMsaId);

  let newPath;
  if (newMsa && oldMsa) {
    newPath = path.replace(oldMsa.alias, newMsa.alias);
  } else if (newMsa) {
    const [nil, page, ...rest] = path.split('/');
    newPath = [nil, page, newMsa.alias, ...rest].join('/');
  } else if (oldMsa) {
    newPath = path.replace(oldMsa.alias, '').replace('//', '/');
  } else {
    newPath = path;
  }
  return newPath;
}

export function* doRedirectPricingPages(client, { path, newMsaId }) {
  if (!newMsaId) {
    yield put(replacePath('/'));
    return;
  }

  const newPath = yield call(doBuildNewPath, client, { path, newMsaId });
  try {
    const newCmsData = yield call(fetchEntityIfNeeded, cms, [client, newPath]);
    if (newCmsData) {
      yield put(replacePath(newPath));
    }
  } catch (err) {
    if (err instanceof DALNotFoundError) {
      yield put(replacePath(`/pricing/${GET_CLASSPASS_DEFAULT_PROMOTION_ALIAS}`));
      yield put(
        addGenericNotification('Uh oh! That promotion isn’t valid in your city. Choose a plan below to get started.', {
          timeout: 0,
        })
      );
      return;
    }
    throw err;
  }
}

export function* doRedirectGenericPages(client, { path, newMsaId }) {
  const newPath = yield call(doBuildNewPath, client, { path, newMsaId });
  try {
    const newCmsData = yield call(fetchEntityIfNeeded, cms, [client, newPath]);
    if (newCmsData) {
      yield put(replacePath(newPath));
    }
  } catch (err) {
    if (err instanceof DALNotFoundError) {
      yield put(
        addGenericNotification('Please provide a valid location.', {
          timeout: 0,
        })
      );
      return;
    }
    throw err;
  }
}

export function* doChangeLocation(client, { lat, lon }) {
  try {
    const [locationData = {}] = yield all([
      call(fetchEntityIfNeeded, glsLocation, [client, lat, lon]),
      call(persistUserLocation, lat, lon),
    ]);
    const msa = locationData.msa || {};
    const newMsaId = msa.id && msa.package_purchase ? msa.id : yield call(openLocationModal);

    yield put(setMsaId(newMsaId));

    // TODO: decouple redirect behavior from location modification
    // or at least make no-redirect the default case
    const path = yield select(pathSelector);
    if (new RegExp('^/pricing/').test(path)) {
      yield call(doRedirectPricingPages, client, { path, newMsaId });
    } else if (!new RegExp('^/locations/').test(path) && !/^\/invite/.test(path)) {
      yield call(doRedirectGenericPages, client, { path, newMsaId });
    }
  } catch (err) {
    yield all([
      call(logger.error, 'LocationSelector: Could not change location using lat, lon', `${lat}, ${lon}`, err),
      put(addFailureNotification(messages.genericMessage)),
    ]);
  }
}

export function* loadSaga(client) {
  yield all([call(fetchEntityIfNeeded, msas, [client]), call(getUserLocation, client)]);
}

export function* watchSaga(client) {
  yield takeEvery(changeLocation().type, doChangeLocation, client);
}
